import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { frameTitle, isFrameObject } from "../../editor/frame/frameContext";
import type { WbBoardScene } from "../../lib/board/wb-board-scene";
import { IconFrame } from "./BoardToolIcons";

type FrameListItem = { id: string; title: string };

export type BoardFramesPresentationProps = {
  canControl: boolean;
  active: boolean;
  isPresenter: boolean;
  disabled?: boolean;
  onToggle: () => void;
};

type Props = {
  ready: boolean;
  sceneRef: React.RefObject<InstanceType<typeof WbBoardScene> | null>;
  sceneTick?: number;
  selectedIds?: string[];
  onNavigate: (frameId: string) => void;
  className?: string;
  presentation?: BoardFramesPresentationProps;
};

type PopoverPos = { top: number; left: number; maxHeight: number };

function listFrames(scene: InstanceType<typeof WbBoardScene> | null): FrameListItem[] {
  if (!scene) return [];
  return scene
    .getObjects()
    .filter(isFrameObject)
    .map((o) => ({ id: o.id, title: frameTitle(o) }))
    .sort((a, b) => a.title.localeCompare(b.title, "ru", { sensitivity: "base" }));
}

function computePopoverPos(
  btn: HTMLElement,
  pop: HTMLElement | null
): PopoverPos {
  const rect = btn.getBoundingClientRect();
  const vv = window.visualViewport;
  const viewL = vv?.offsetLeft ?? 0;
  const viewT = vv?.offsetTop ?? 0;
  const viewW = vv?.width ?? window.innerWidth;
  const viewH = vv?.height ?? window.innerHeight;
  const margin = 8;
  const popW = pop?.offsetWidth || Math.min(288, viewW - margin * 2);
  const popH = pop?.offsetHeight || 240;

  let left = rect.right - popW;
  left = Math.min(Math.max(viewL + margin, left), viewL + viewW - popW - margin);

  let top = rect.bottom + 4;
  const spaceBelow = viewT + viewH - top - margin;
  const spaceAbove = rect.top - viewT - margin;
  if (popH > spaceBelow && spaceAbove > spaceBelow) {
    top = Math.max(viewT + margin, rect.top - 4 - Math.min(popH, spaceAbove));
  }

  return {
    top,
    left,
    maxHeight: Math.max(140, viewT + viewH - top - margin),
  };
}

export function BoardFramesButton({
  ready,
  sceneRef,
  sceneTick = 0,
  selectedIds = [],
  onNavigate,
  className = "wb-btn wb-btn--ghost wb-btn--sm",
  presentation,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const frames = useMemo(() => listFrames(sceneRef.current), [sceneRef, sceneTick, open, ready]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return frames;
    return frames.filter((f) => f.title.toLowerCase().includes(q));
  }, [frames, query]);

  const presentActive = Boolean(presentation?.active && presentation.isPresenter);
  const showPresentation = Boolean(presentation?.canControl);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const btn = btnRef.current;
    if (!btn) return;
    setPos(computePopoverPos(btn, popoverRef.current));
    const id = requestAnimationFrame(() => {
      if (!btnRef.current) return;
      setPos(computePopoverPos(btnRef.current, popoverRef.current));
    });
    return () => cancelAnimationFrame(id);
  }, [open, filtered.length, query, showPresentation, presentActive]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();

    function onDoc(ev: PointerEvent) {
      const t = ev.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    function onReposition() {
      if (!btnRef.current) return;
      setPos(computePopoverPos(btnRef.current, popoverRef.current));
    }

    document.addEventListener("pointerdown", onDoc, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.visualViewport?.addEventListener("resize", onReposition);
    window.visualViewport?.addEventListener("scroll", onReposition);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.visualViewport?.removeEventListener("resize", onReposition);
      window.visualViewport?.removeEventListener("scroll", onReposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <div className="wb-board-frames" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className={`${className}${open ? " is-active" : ""}${presentActive ? " is-presenting" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Фреймы на доске"
        disabled={!ready}
        onClick={() => setOpen((v) => !v)}
      >
        <IconFrame className="wb-board-frames__icon" />
        <span className="wb-board-frames__label">Фреймы</span>
        {frames.length > 0 ? (
          <span className="wb-board-frames__count" aria-hidden>
            {frames.length}
          </span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="wb-board-frames__popover wb-board-frames__popover--fixed"
              role="dialog"
              aria-label="Список фреймов"
              style={
                pos
                  ? { top: pos.top, left: pos.left, maxHeight: pos.maxHeight }
                  : { top: 0, left: 0, visibility: "hidden" }
              }
            >
              <input
                ref={inputRef}
                type="search"
                className="wb-board-frames__search"
                placeholder="Поиск по названию…"
                value={query}
                onChange={(ev) => setQuery(ev.target.value)}
                onKeyDown={(ev) => ev.stopPropagation()}
              />
              {filtered.length === 0 ? (
                <p className="wb-board-frames__empty">
                  {frames.length === 0 ? "На доске пока нет рамок" : "Ничего не найдено"}
                </p>
              ) : (
                <ul className="wb-board-frames__list" role="listbox">
                  {filtered.map((f) => {
                    const active = selectedIds.includes(f.id);
                    return (
                      <li key={f.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={`wb-board-frames__item${active ? " is-active" : ""}`}
                          onClick={() => {
                            const id = f.id;
                            setOpen(false);
                            requestAnimationFrame(() => {
                              requestAnimationFrame(() => onNavigate(id));
                            });
                          }}
                        >
                          {f.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {showPresentation && presentation ? (
                <div className="wb-board-frames__footer">
                  <button
                    type="button"
                    className={`wb-board-frames__present${presentActive ? " is-active" : ""}`}
                    title="Презентация по фреймам"
                    disabled={Boolean(presentation.disabled) || !ready}
                    onClick={() => {
                      presentation.onToggle();
                      if (!presentActive) setOpen(false);
                    }}
                  >
                    {presentActive ? "Остановить презентацию" : "Презентация"}
                  </button>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
